import { useFeatureFlagEnabled } from '@/features/flags/posthog-compat';
import {
  projectsFlag,
  projectsWebFlag,
  workLetterArtifactFlag,
} from '@heidi/agents/flags';
import { useBooleanFlag } from '@heidi/runtime/feature-flags';
import { isTauri } from '@heidi/runtime/platform';
import { useIsV2HeidiSuggestsFlagEnabled } from '@heidi/session/flags';

export const HEIDI_II_MASTER_FLAG_KEY = 'release-enable-heidi-ii';

/**
 * The Heidi Work umbrella: PostHog `release-enable-heidi-ii` OR the legacy
 * Flagsmith Projects keys (`release-scribe-enable-projects` on desktop,
 * `release-scribe-enable-projects-web` on web). All three reads run
 * unconditionally to keep hook order stable.
 *
 * The single Work gate on the frontend: every Work-scoped getter below and the
 * `@heidi/agents/data` inbox gate (via `configureAgentsData`) follow it, so a
 * PostHog-only user never sees Work without its routines or inbox.
 *
 * Turning Work off takes BOTH providers: PostHog `release-enable-heidi-ii` off
 * AND the platform's Flagsmith Projects key off. Flipping one leaves every
 * surface above live for users the other still enables.
 *
 * Lives in this leaf module (re-exported by the `@/features/flags` barrel) so
 * leaf flag modules can compose it without importing the barrel.
 */
export const useIsProjectsEnabled = (): boolean => {
  const isHeidiIIMasterEnabled = useFeatureFlagEnabled(HEIDI_II_MASTER_FLAG_KEY) ?? false;
  const isProjectsFlagEnabled = useBooleanFlag(
    projectsFlag.key,
    projectsFlag.defaultValue
  );
  const isProjectsWebEnabled = useBooleanFlag(
    projectsWebFlag.key,
    projectsWebFlag.defaultValue
  );
  const isLegacyProjectsEnabled = isTauri()
    ? isProjectsFlagEnabled
    : isProjectsWebEnabled;
  return isHeidiIIMasterEnabled || isLegacyProjectsEnabled;
};

/** Contextual thinking phrases (`turn.loader_phrases`). No dedicated flag. */
export const useIsProjectsContextualLoaderPhrasesEnabled = (): boolean =>
  useIsProjectsEnabled();

/**
 * Agent-driven session recording — the `ui.session_recording` event dispatcher,
 * the global recorder overlay and the artifact-header controls. No dedicated flag.
 */
export const useIsAgentSessionRecordingEnabled = (): boolean => useIsProjectsEnabled();

/** Routines/Workflows UI (BE `is_workflows_enabled` mirrors). */
export const useIsWorkflowsEnabled = (): boolean => useIsProjectsEnabled();

/** Session routine runs. The legacy child key is not read. */
export const useIsSessionRoutineRunsEnabled = (): boolean => useIsProjectsEnabled();

/** Session V2 "Heidi suggests" card: Work umbrella AND `release-scribe-v2-heidi-suggests`. */
export const useIsV2HeidiSuggestsEnabled = (): boolean => {
  const isProjectsEnabled = useIsProjectsEnabled();
  const isHeidiSuggestsFlagEnabled = useIsV2HeidiSuggestsFlagEnabled();
  return isProjectsEnabled && isHeidiSuggestsFlagEnabled;
};

export const useIsWorkLetterArtifactEnabled = (): boolean => {
  const isProjectsEnabled = useIsProjectsEnabled();
  const isWorkLetterArtifactEnabled = useBooleanFlag(
    workLetterArtifactFlag.key,
    workLetterArtifactFlag.defaultValue
  );
  return isProjectsEnabled && isWorkLetterArtifactEnabled;
};
